import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'

import {
    AccountOpeningPolicyRequest, AndroidBuildRecordStatus, AndroidBuildTriggerResult, AppDownloadLinksRequest, GetOnboardingSlidesResponse,
    IndemnityStatementRequest,
    InstitutionProfileDetails,
    InstitutionProfileRequest,
    OnboardingPolicyConfig,
    OnboardingPolicyDetails,
    OnboardingSlideRequest,
    PaginationParams,
    ProductsQueryParams,
    ResendTeammateInviteRequest,
    SelectThemeVariant,
    TeammateInviteRequest,
    ThemeConfigurationRequest,
    TransactionPolicyRequest,
} from 'src/model/v2/interface/params'

import VisibilityContext from 'src/provider/state-manager/visibilityProvider'

import { catchErrMsg, erroMessage, loglite, requestMessage } from 'src/utils/utility'

import apiCaller from '../../interceptor'
import customerCaller from '../../interceptor/customerInterceptor'
import {hashPassword} from "src/utils/passwordCrypto";

const ConfigurationApiContext = React.createContext<any>(null)

const PROFILE_CACHE_KEY = 'institutionProfileData'
const TEAM_CACHE_KEY = 'institutionTeammatesData'
const ONBOARDING_POLICY_CACHE_KEY = 'institutionOnboardingPolicyData'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const ADMIN_PROFILE_CACHE_KEY = 'adminProfileData'

// Backend role constants (must match ADMIN_ROLE / INST_SUPER_ADMIN_ROLE / USER_ROLE)
export const TEAM_ROLE_LABELS: Record<string, string> = {
    Admin: 'Admin',
    ISA: 'Institution Super Admin',
    User: 'User',
    SA: 'Super Admin'
}

export interface AdminProfileDetails {
    fullName: string
    email: string
    username: string
    phoneNumber: string
    role: string
    isActive: boolean
}

export interface CachedTeammate {
    id: string | number
    firstName: string
    lastName: string
    userName: string
    email: string
    phone: string
    role: string
    status: 'Invited' | 'Active'
}
export interface InstitutionExportColumns {
    enabled: boolean
    favIcon: string | null
    stamp: string | null
    design: string | null
    appName: string | null
    address: string | null
    website: string | null
    correspondenceEmail: string | null
    correspondencePhone: string | null
    emailSenderID: string | null
    goLiveDate: string | null
    primaryColour: string | null
    secondaryColour: string | null
    primaryColour2: string | null
    secondaryColour2: string | null
    hasLiveChat: boolean | null
    usePINforLogin: boolean | null
    hasTransfer: boolean | null
    useIdemityForAccountLimitChange: boolean | null
    accountLimitIdemity: string | null
    useVerifyMeForOnboarding: boolean
    useAirtimeAsBills: boolean | null
    accountNumberLength: number | null
    paymentItemIsHidden: boolean | null
    shouldLockNewProfile: boolean | null
    defaultOneTimeTransactionLimit: number
    defaultDailyTransactionLimit: number
    testAccountNumber: string | null
    testAccountCustomerID: string | null
    testAccountUsername: string | null
    quickTellerURL: string | null
    institutionType: string | null
    pilotPhaseMaximumUserCount: number | null
    implementationPhase: string | null
    defaultAccountOpeningProductCode: string | null
    defaultAccountOfficerCode: string | null
    ussdCode: string | null
    allowedAccountProductCodes: string | null
    disallowedAccountProductCodes: string | null
    disallowedLoanProductCodes: string | null
    disallowedFixedDepositProductCodes: string | null
    phoneNumberLength: number | null
    transferGateway: string | null
    maximumOneTimeTransactionLimit: number
    maximumDailyTransactionLimit: number
    newDeviceSpendingCap: number | null
    customizedUrl: string | null
    twoFactorThresholdAmount: number | null
    showTransactions: boolean | null
    termsAndConditionsUrl: string | null
    privacyPolicyUrl: string | null
    loanPolicyUrl: string | null
    appzoneUsername: string | null
    appzoneAccountNumber: string | null
    appzoneCustomerId: string | null
    hideWithdrawableBalance: boolean | null
    hideAvailableBalance: boolean | null
    isBaasInstitution: boolean | null
    billsPaymentGateway: string | null
    newCustomerOnboardingType: string | null
    existingCustomerOnboardingType: string | null
    hasWeb: boolean | null
    hasMobile: boolean | null
    hasChangedSinceLastBuild: boolean | null
    defaultCurrency: string | null
    cardFee: number | null
    extraAccountsProductCodes: string | null
    extraAccountOfficerCode: string | null
    usePhoneNumberAsAccountNumber: boolean | null
    useVerifyMeForTransactions: boolean | null
    cardRequestType: string | null
    ignoreSignatureForOnboarding: boolean | null
    usePremiumBvn: boolean
    useSmsWebhook: boolean
    isActive: boolean
    allowArbitraryPhoneNumber: boolean
    allowIdVerificationWithMetadata: boolean
    cardStyle: string | null
    typography: string | null
    cornerRadius: string | null
    language: string | null
    transferGatewayEnum: string | null
}
export interface InstitutionListItem {
    id: number
    name: string
    bankOneCode: string
    institutionCode: number
    alias: string | null
    logo: string | null
    isActive: boolean
    setupDate: string | null
    export?: InstitutionExportColumns | null
}

export interface InstitutionDetail {
    institutionId: number
    name: string
    logo: string | null
    alias: string | null
    country: string | null
    language: string | null
    setupDate: string | null
    institutionCode: string | null
    correspondencePhone: string | null
    institutionType: string | null
    isActive: boolean
    totalUsers: number
    isBaas: boolean
    correspondenceEmail: string | null
}
export interface InstitutionsMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}
export interface TeammatesMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}
export interface AuditLogListItem {
    sn: number
    id: number
    userId: number
    fullName: string
    profileId: number | null
    customerFullName: string | null
    role: string
    action: string
    model: string
    activityType: string
    performedAt: string
}

export interface AuditLogsMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

export interface AuditLogDetailData {
    userId: number
    fullName: string
    role: string
    action: string
    model: string
    dataBefore: string
    dataAfter: string
    activityType: string
    performedAt: string
}

export interface AuditLogsQueryParams {
    PageLimit?: number
    PageNumber?: number
    FetchAll?: boolean
    search?: string
    actions?: string[]
    modules?: string[]
    roles?: string[]
    from?: string
    to?: string
    activityType?: string
}

const TEAM_PAGE_SIZE = 5

function mapTeammate(item: any): CachedTeammate {
    return {
        id: item.userID,
        firstName: item.firstName,
        lastName: item.lastName,
        userName: item.username,
        email: item.email,
        phone: item.phone ?? '',
        role: item.role,
        status: item.status,
    }
}

function buildAuditLogsQuery(params: AuditLogsQueryParams): string {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return
        if (Array.isArray(value)) {
            value.forEach((v) => search.append(key, String(v)))
        } else {
            search.append(key, String(value))
        }
    })
    return search.toString()
}

export const ConfigurationApiProvider = (props: any) => {

    const { loader, notifier } = useContext(VisibilityContext)

    const [institutionProfile, setInstitutionProfile] = useState<InstitutionProfileDetails | null>(null)
    const hasFetchedProfile = useRef(false)

    const [teammates, setTeammates] = useState<CachedTeammate[]>([])
    const [teammatesMeta, setTeammatesMeta] = useState<TeammatesMeta | null>(null)
    const hasFetchedTeammates = useRef(false)

    const [onboardingPolicy, setOnboardingPolicyState] = useState<OnboardingPolicyDetails | null>(null)
    const hasFetchedOnboardingPolicy = useRef(false)
    const onboardingPolicyCacheTimestamp = useRef<number | null>(null)

    // Cache timestamp for TTL checking
    const cacheTimestamp = useRef<number | null>(null)
    const [adminProfile, setAdminProfile] = useState<AdminProfileDetails | null>(null)
    const hasFetchedAdminProfile = useRef(false)
    const adminProfileCacheTimestamp = useRef<number | null>(null)

    function isAdminProfileCacheValid(): boolean {
        if (!adminProfileCacheTimestamp.current) return false
        return Date.now() - adminProfileCacheTimestamp.current < CACHE_TTL
    }

    // NOTE: request() is stable (no external deps besides loader/notifier from
    // context, which the app treats as stable), so it's safe to reference from
    // useCallback-wrapped functions below without listing it in every dep array.
    const request = useCallback(async function request<T = any>(
        fn: () => Promise<any>,
        label: string
    ): Promise<T | false | null> {
        try {
            loader(true)
            const response = await fn()
            const { data, status } = response
            loglite({ data }, label)

            if (status === 204 || data === undefined || data === null || data === '') {
                return true as unknown as T
            }

            if (data?.responseCode && data.responseCode !== '00') {
                notifier.show(requestMessage(data, data.responseDescription))
                return false
            }

            return (data?.data ?? data) as T
        } catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        } finally {
            loader(false)
        }
    }, [loader, notifier])

    function isCacheValid(): boolean {
        if (!cacheTimestamp.current) return false
        return Date.now() - cacheTimestamp.current < CACHE_TTL
    }

    function isOnboardingPolicyCacheValid(): boolean {
        if (!onboardingPolicyCacheTimestamp.current) return false
        return Date.now() - onboardingPolicyCacheTimestamp.current < CACHE_TTL
    }

    const cacheProfile = useCallback((data: InstitutionProfileDetails) => {
        setInstitutionProfile(data)
        hasFetchedProfile.current = true
        cacheTimestamp.current = Date.now()
        try {
            localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now()
            }))
        } catch {
            // storage full/unavailable — non-fatal, in-memory state still works
        }
    }, [])

    const cacheTeammates = useCallback((list: CachedTeammate[], meta?: TeammatesMeta) => {
        setTeammates(list)
        hasFetchedTeammates.current = true
        if (meta) setTeammatesMeta(meta)
        try {
            localStorage.setItem(TEAM_CACHE_KEY, JSON.stringify({
                data: list,
                meta,
                timestamp: Date.now()
            }))
        } catch {
            // storage full/unavailable — non-fatal, in-memory state still works
        }
    }, [])

    const cacheAdminProfile = useCallback((data: AdminProfileDetails) => {
        setAdminProfile(data)
        hasFetchedAdminProfile.current = true
        adminProfileCacheTimestamp.current = Date.now()
        try {
            localStorage.setItem(ADMIN_PROFILE_CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now(),
            }))
        } catch {
            // storage full/unavailable — non-fatal, in-memory state still works
        }
    }, [])

    const cacheOnboardingPolicy = useCallback((data: OnboardingPolicyDetails) => {
        setOnboardingPolicyState(data)
        hasFetchedOnboardingPolicy.current = true
        onboardingPolicyCacheTimestamp.current = Date.now()
        try {
            localStorage.setItem(ONBOARDING_POLICY_CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now()
            }))
        } catch {
            // storage full/unavailable — non-fatal, in-memory state still works
        }
    }, [])

    // ---- Institution profile ----

    const getInstitutionProfile = useCallback(async (force = false) => {
        // 1. In-memory cache with TTL check
        if (institutionProfile && !force && hasFetchedProfile.current && isCacheValid()) {
            return institutionProfile
        }

        // 2. localStorage cache with TTL check
        if (!force) {
            try {
                const cached = localStorage.getItem(PROFILE_CACHE_KEY)
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached)
                    if (Date.now() - timestamp < CACHE_TTL) {
                        setInstitutionProfile(data)
                        hasFetchedProfile.current = true
                        cacheTimestamp.current = timestamp
                        return data
                    } else {
                        // Cache expired, remove it
                        localStorage.removeItem(PROFILE_CACHE_KEY)
                    }
                }
            } catch {
                // corrupted cache entry — fall through to network
            }
        }

        // 3. network — first real load, or a forced refresh
        const data = await request<InstitutionProfileDetails>(
            () => customerCaller.get(`admin/Institutions/Profile`),
            'get institution profile API'
        )
        if (data) cacheProfile(data)
        return data
    }, [institutionProfile, request, cacheProfile])

    const createInstitution = useCallback(async (payload: {
        institutionName: string
        country: string
        domainType: string
        domain: string
        emailSenderID: string
        institutionCode: string
        bankOneToken: string
        adminFirstName: string
        adminLastName: string
        adminUsername: string
        adminEmail: string
        adminPhoneNumber: string
    }) => {
        const result = await request(
            () => customerCaller.post(`admin/Institutions/create`, payload),
            'create institution API'
        )
        return result !== false && result !== null
    }, [request])

    const updateInstitutionProfile = useCallback(async (payload: InstitutionProfileRequest) => {
        const formData = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value as any)
            }
        })

        try {
            loader(true)
            await customerCaller.patch(`admin/Institutions/Profile`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })

            // Fetch fresh data after update to get the latest from server
            const freshData = await getInstitutionProfile(true)

            if (freshData) {
                // Update cache with fresh data
                cacheProfile(freshData)
            }

            return true
        } catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return false
        } finally {
            loader(false)
        }
    }, [loader, notifier, getInstitutionProfile, cacheProfile])

    const getAdminProfile = useCallback(async (force = false) => {
        if (adminProfile && !force && hasFetchedAdminProfile.current && isAdminProfileCacheValid()) {
            return adminProfile
        }

        if (!force) {
            try {
                const cached = localStorage.getItem(ADMIN_PROFILE_CACHE_KEY)
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached)
                    if (Date.now() - timestamp < CACHE_TTL) {
                        setAdminProfile(data)
                        hasFetchedAdminProfile.current = true
                        adminProfileCacheTimestamp.current = timestamp
                        return data
                    }
                    localStorage.removeItem(ADMIN_PROFILE_CACHE_KEY)
                }
            } catch {
                // corrupted cache entry — fall through to network
            }
        }

        const data = await request<AdminProfileDetails>(
            () => customerCaller.get(`admin/Institutions/AdminProfile`),
            'get admin profile API'
        )
        if (data) cacheAdminProfile(data)
        return data
    }, [adminProfile, request, cacheAdminProfile])

    const changePassword = useCallback(async (payload: { currentPassword: string; newPassword: string }) => {
        const [hashedCurrent, hashedNew] = await Promise.all([
            hashPassword(payload.currentPassword),
            hashPassword(payload.newPassword),
        ])

        const result = await request(
            () => customerCaller.post(`admin/InstitutionOnboarding/ChangePassword`, {
                newPassword: hashedNew,
                currentPassword: hashedCurrent,
            }),
            'change password API'
        )
        // Endpoint returns a bare 200 on success — request() already treats
        // an empty/undefined body as success, so this just needs to reject
        // the explicit false/null failure cases.
        return result !== false && result !== null
    }, [request])

    // ---- Theme ----

    const selectThemeVariant = useCallback(async (payload: SelectThemeVariant) => {
        return request(
            () => customerCaller.patch(`admin/Institutions/ThemeVariant`, payload),
            'select theme variant API'
        )
    }, [request])

    const updateThemeConfiguration = useCallback(async (payload: ThemeConfigurationRequest) => {
        return request(
            () => customerCaller.patch(`admin/Institutions/ThemeConfiguration`, payload),
            'update theme configuration API'
        )
    }, [request])

    const updateAppDownloadLinks = useCallback(async (payload: AppDownloadLinksRequest) => {
        return request(
            () => customerCaller.patch(`admin/Institutions/AppDownloadLinks`, payload),
            'update app download links API'
        )
    }, [request])

    // ---- Audit logs ----

    const getAuditLogs = useCallback(async (params: AuditLogsQueryParams) => {
        const queryString = buildAuditLogsQuery(params)
        const result = await request<{ meta: AuditLogsMeta; items: AuditLogListItem[] }>(
            () => customerCaller.get(`admin/Institutions/AuditLogs${queryString ? `?${queryString}` : ''}`),
            'get audit logs API'
        )

        if (result && result.items) {
            return { items: result.items, meta: result.meta }
        }

        return { items: [], meta: null }
    }, [request])

    const getAuditLogDetail = useCallback(async (auditLogId: number, activityType?: string) => {
        const result = await request<AuditLogDetailData>(
            () => customerCaller.get(`admin/Institutions/AuditLogDetail/${auditLogId}`, {
                params: activityType ? { activityType } : undefined,
            }),
            'get audit log detail API'
        )

        return result || null
    }, [request])

    const getAuditLogModels = useCallback(async () => {
        const result = await request<string[]>(
            () => customerCaller.get(`admin/Institutions/AuditLogModels`),
            'get audit log models API'
        )

        return result || []
    }, [request])

    const getAuditLogActions = useCallback(async () => {
        const result = await request<string[]>(
            () => customerCaller.get(`admin/Institutions/AuditLogActions`),
            'get audit log actions API'
        )

        return result || []
    }, [request])

    // ---- Auto deployment ----

    const saveAutoDeploymentCredentials = useCallback(async (credentialJson: string) => {
        const result = await request(
            () => customerCaller.post(`admin/Institutions/AutoDeploymentCredentials`, {credential: JSON.stringify(credentialJson)}),
            'save auto deployment credentials API'
        )
        return result !== false && result !== null
    }, [request])

    /**
     * `AlreadyPending`/`InstitutionNotFound` are still 200 OK responses (not thrown
     * errors), so the shared `request()` wrapper wouldn't surface them — handled
     * explicitly here so the caller always sees a message for a non-queued outcome.
     */
    const triggerAutoDeployment = useCallback(async () => {
        const result = await request<AndroidBuildTriggerResult>(
            () => customerCaller.post(`admin/Institutions/TriggerAutoDeployment`),
            'trigger auto deployment API'
        )
        if (!result) return result

        if (result.outcome === 'AlreadyPending') {
            notifier.show(erroMessage('A build is already queued or in progress for this institution.'))
        } else if (result.outcome === 'InstitutionNotFound') {
            notifier.show(erroMessage('Institution could not be found.'))
        }

        return result
    }, [request, notifier])

    /**
     * A 404 here means the institution has never had a build triggered — not an
     * error. Bypasses the shared `request()` wrapper so that case resolves to
     * `null` quietly instead of firing an error toast. Deliberately skips the
     * global `loader()` — this is polled every few seconds in the background
     * while a build is pending, and flashing the app-wide spinner on every tick
     * would be disruptive.
     */
    const getAutoDeploymentStatus = useCallback(async () => {
        try {
            const response = await customerCaller.get(`admin/Institutions/AutoDeploymentStatus`)
            return (response.data ?? null) as AndroidBuildRecordStatus | null
        } catch (err: any) {
            if (err?.response?.status === 404) return null
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }, [notifier])

    // ---- Onboarding slides ----

    const updateOnboardingSlides = useCallback(async (payload: OnboardingSlideRequest[]) => {
        const formData = new FormData()
        payload.forEach((slide, index) => {
            Object.entries(slide).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(`request[${index}].${key}`, value as any)
                }
            })
        })
        return request(
            () => customerCaller.patch(`admin/Institutions/OnboardingSlides`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }),
            'update onboarding slides API'
        )
    }, [request])

    const deleteOnboardingSlide = useCallback(async (slideId: number) => {
        // NOTE: switched from apiCaller -> customerCaller.
        // Every Institutions endpoint lives on the customer-service upstream
        // (see apigateway prefix .../customer-service/api/v1/...). apiCaller's
        // baseURL doesn't include that segment, so this 404'd the same way
        // the policy/products/features endpoints below did.
        return request(
            () => customerCaller.delete(`admin/Institutions/DeleteOnboardingSlide/${slideId}`),
            'delete onboarding slide API'
        )
    }, [request])

    const getInstitutions = useCallback(async (params: {
        Search?: string
        IsActive?: boolean
        HasWeb?: boolean
        HasMobile?: boolean
        From?: string
        To?: string
        PageLimit?: number
        PageNumber?: number
        FetchAll?: boolean
        Download?: boolean
    }) => {
        const result = await request<{ meta: InstitutionsMeta; items: InstitutionListItem[] }>(
            () => customerCaller.get(`admin/Institutions/GetInstitutions`, { params }),
            'get institutions API'
        )

        if (result && result.items) {
            return { items: result.items, meta: result.meta }
        }

        return { items: [], meta: null }
    }, [request])

    const getInstitutionDetail = useCallback(async (institutionId: number) => {
        const result = await request<InstitutionDetail>(
            () => customerCaller.get(`admin/Institutions/InstitutionDetail`, {
                params: { institutionId },
                headers: { 'X-Institution-Id': String(institutionId) },
            }),
            'get institution detail API'
        )

        return result || null
    }, [request])

    // ---- Teammates ----

    /**
     * GET Teammates is now live. Pagination is 5-per-page server-side.
     *
     * Cache order: in-memory → localStorage → network. `force` (or asking for
     * a page we don't have cached) always goes to the network.
     */
    const getTeammates = useCallback(async (pageNumber = 1, force = false) => {
        if (
            !force &&
            teammates.length &&
            hasFetchedTeammates.current &&
            teammatesMeta?.currentPageNumber === pageNumber
        ) {
            return { items: teammates, meta: teammatesMeta }
        }

        if (!force && pageNumber === 1) {
            try {
                const cached = localStorage.getItem(TEAM_CACHE_KEY)
                if (cached) {
                    const { data, meta } = JSON.parse(cached)
                    setTeammates(data)
                    if (meta) setTeammatesMeta(meta)
                    hasFetchedTeammates.current = true
                    return { items: data, meta }
                }
            } catch {
                // corrupted cache entry — fall through to network
            }
        }

        const result = await request<{ meta: TeammatesMeta; items: any[] }>(
            () => customerCaller.get(`admin/Institutions/GetTeammates`, {
                params: { PageLimit: TEAM_PAGE_SIZE, PageNumber: pageNumber } as PaginationParams,
            }),
            'get teammates API'
        )

        if (result && result.items) {
            const mapped = result.items.map(mapTeammate)
            cacheTeammates(mapped, result.meta)
            return { items: mapped, meta: result.meta }
        }

        return { items: teammates, meta: teammatesMeta }
    }, [teammates, teammatesMeta, request, cacheTeammates])

    const inviteTeammate = useCallback(async (payload: TeammateInviteRequest) => {
        const result = await request<any>(
            () => customerCaller.post(`admin/Institutions/InviteTeammate`, payload),
            'invite teammate API'
        )

        if (!result) return null // false (validation) or null (network error) — notifier already fired

        // Optimistic local addition — gives the illusion of an instant update
        // while the real list is out of sync with the server.
        const newMember: CachedTeammate = {
            id: result?.id ?? result?.userId ?? result?.data?.id ?? Date.now().toString(),
            firstName: payload.firstName,
            lastName: payload.lastName,
            userName: payload.username,
            email: payload.email,
            phone: payload.phone,
            role: payload.role,
            status: 'Invited',
        }

        cacheTeammates([newMember, ...teammates])

        // Reconcile with the server in the background (real IDs, real status)
        // without blocking the optimistic UI update above.
        getTeammates(1, true).catch(() => {})

        return newMember
    }, [request, teammates, cacheTeammates, getTeammates])

    const resendTeammateInvite = useCallback(async (payload: ResendTeammateInviteRequest) => {
        const result = await request(
            () => customerCaller.post(`admin/Institutions/ResendTeammateInvite`, payload),
            'resend teammate invite API'
        )
        return result !== false && result !== null
    }, [request])

    // ---- Policies ----

    const updateAccountOpeningPolicy = useCallback(async (payload: AccountOpeningPolicyRequest) => {
        // NOTE: switched from apiCaller -> customerCaller, same reason as
        // deleteOnboardingSlide above.
        return request(
            () => customerCaller.put(`admin/Institutions/AccountOpeningPolicy`, payload),
            'update account opening policy API'
        )
    }, [request])

    const updateTransactionPolicy = useCallback(async (payload: TransactionPolicyRequest) => {
        const formData = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value as any)
            }
        })
        return request(
            () => customerCaller.put(`admin/Institutions/TransactionPolicy`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }),
            'update transaction policy API'
        )
    }, [request])

    const updateApplicationFeatures = useCallback(async (payload: string[]) => {
        return request(
            () => customerCaller.put(`admin/Institutions/ApplicationFeatures`, payload),
            'update application features API'
        )
    }, [request])

    /**
     * GET /Onboarding/Policy lives on customerCaller (customer-service base URL),
     * matching the curl this was verified against. Note this is a different base
     * from the POST below (apiCaller) — same split as profile/teammates reads
     * vs. other policy writes elsewhere in this file; not a copy-paste slip.
     */
    const getOnboardingPolicy = useCallback(async (force = false) => {
        if (
            onboardingPolicy &&
            !force &&
            hasFetchedOnboardingPolicy.current &&
            isOnboardingPolicyCacheValid()
        ) {
            return onboardingPolicy
        }

        if (!force) {
            try {
                const cached = localStorage.getItem(ONBOARDING_POLICY_CACHE_KEY)
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached)
                    if (Date.now() - timestamp < CACHE_TTL) {
                        setOnboardingPolicyState(data)
                        hasFetchedOnboardingPolicy.current = true
                        onboardingPolicyCacheTimestamp.current = timestamp
                        return data
                    } else {
                        localStorage.removeItem(ONBOARDING_POLICY_CACHE_KEY)
                    }
                }
            } catch {
                // corrupted cache entry — fall through to network
            }
        }

        const data = await request<OnboardingPolicyDetails>(
            () => customerCaller.get(`Onboarding/Policy`),
            'get onboarding policy API'
        )
        if (data) cacheOnboardingPolicy(data)
        return data
    }, [onboardingPolicy, request, cacheOnboardingPolicy])

    const setOnboardingPolicy = useCallback(async (payload: OnboardingPolicyConfig) => {
        // NOTE: switched from apiCaller -> customerCaller. This is what was
        // causing the 404 on POST admin/Institutions/onboarding/policy —
        // apiCaller's baseURL is missing the `customer-service` gateway
        // segment that every Institutions endpoint actually lives under
        // (confirmed working example: .../customer-service/api/v1/admin/Institutions/ThemeVariant).
        const result = await request(
            () => customerCaller.post(`admin/Institutions/onboarding/policy`, payload),
            'set onboarding policy API'
        )

        // Keep the cache in sync with what we just saved so a subsequent
        // getOnboardingPolicy() call (e.g. navigating back to this step)
        // doesn't need a network round trip to reflect the change.
        if (result !== false && result !== null) {
            cacheOnboardingPolicy(payload as OnboardingPolicyDetails)
        }

        return result
    }, [request, cacheOnboardingPolicy])

    const addIndemnityStatement = useCallback(async (payload: IndemnityStatementRequest) => {
        return request(
            () => customerCaller.post(`admin/Institutions/IndemnityStatement`, payload),
            'add indemnity statement API'
        )
    }, [request])

    // ---- Lookups / listings ----

    const getProducts = useCallback(async (params?: ProductsQueryParams) => {
        return request(
            () => customerCaller.get(`admin/Institutions/Products`, { params }),
            'get products API'
        )
    }, [request])

    const getAccountOfficers = useCallback(async (params?: PaginationParams) => {
        // NOTE: switched from apiCaller -> customerCaller, same reason as above.
        // Flag to backend/gateway owner if AccountOfficers actually lives on a
        // different upstream than the rest of Institutions — untested here.
        return request(
            () => customerCaller.get(`admin/Institutions/AccountOfficers`, { params }),
            'get account officers API'
        )
    }, [request])

    const getOnboardingSlides = useCallback(async () => {
        return request<GetOnboardingSlidesResponse>(
            () => customerCaller.get(`Configuration/OnboardingSlides`),
            'get onboarding slides API'
        )
    }, [request])

    const getOnboardingChecklist = useCallback(async () => {
        return request(
            () => customerCaller.get(`admin/InstitutionOnboarding/Checklist`),
            'get onboarding checklist API'
        )
    }, [request])

    // Define the context value with all functions — memoized so its identity
    // stays stable across renders that don't actually change any of these
    // values. Without this, every render (e.g. triggered by loader/notifier
    // state elsewhere in the app) creates a brand-new object with brand-new
    // function references, which breaks any `useEffect([someContextFn])`
    // downstream by making it look like a dependency changed on every render.
    const contextValue = useMemo(() => ({
        institutionProfile,
        getInstitutionProfile,
        updateInstitutionProfile,
        teammates,
        teammatesMeta,
        createInstitution,
        getInstitutions,
        selectThemeVariant,
        updateThemeConfiguration,
        updateAppDownloadLinks,
        saveAutoDeploymentCredentials,
        triggerAutoDeployment,
        getAutoDeploymentStatus,
        updateOnboardingSlides,
        deleteOnboardingSlide,
        getOnboardingSlides,
        getTeammates,
        inviteTeammate,
        resendTeammateInvite,
        updateAccountOpeningPolicy,
        updateTransactionPolicy,
        updateApplicationFeatures,
        onboardingPolicy,
        getOnboardingPolicy,
        setOnboardingPolicy,
        addIndemnityStatement,
        getProducts,
        getAccountOfficers,
        getOnboardingChecklist,
        adminProfile,
        getAdminProfile,
        changePassword,
        getInstitutionDetail,
        getAuditLogs,
        getAuditLogDetail,
        getAuditLogModels,
        getAuditLogActions,
    }), [
        institutionProfile,
        getInstitutionProfile,
        updateInstitutionProfile,
        teammates,
        teammatesMeta,
        createInstitution,
        getInstitutions,
        selectThemeVariant,
        updateThemeConfiguration,
        updateAppDownloadLinks,
        saveAutoDeploymentCredentials,
        triggerAutoDeployment,
        getAutoDeploymentStatus,
        updateOnboardingSlides,
        deleteOnboardingSlide,
        getOnboardingSlides,
        getTeammates,
        inviteTeammate,
        resendTeammateInvite,
        updateAccountOpeningPolicy,
        updateTransactionPolicy,
        updateApplicationFeatures,
        onboardingPolicy,
        getOnboardingPolicy,
        setOnboardingPolicy,
        addIndemnityStatement,
        getProducts,
        getAccountOfficers,
        getOnboardingChecklist,
        adminProfile,
        getAdminProfile,
        changePassword,
        getInstitutionDetail,
        getAuditLogs,
        getAuditLogDetail,
        getAuditLogModels,
        getAuditLogActions,
    ])

    return (
        <ConfigurationApiContext.Provider value={contextValue}>
            {props.children}
        </ConfigurationApiContext.Provider>
    )
}

export default ConfigurationApiContext